import { useState, useEffect, useRef } from "react";
import { ArrowRight, Link, Zap } from "lucide-react";
import { Button } from "./button";
import { Card, CardContent, CardHeader, CardTitle } from "./card";
import { Highlighter } from "./highlighter";
import kinesisLogo from "../../media/kinesis_logo.png";

export default function RadialOrbitalTimeline({
    timelineData,
    backgroundColor = "bg-black",
    textColor = "text-white",
    showHeader = false,
    headerTitle = "Your Journey",
    headerDescription = "Track your progress through each milestone with our interactive timeline.",
}) {
    const [expandedItems, setExpandedItems] = useState({});
    const [viewMode, setViewMode] = useState("orbital");
    const [rotationAngle, setRotationAngle] = useState(0);
    const [autoRotate, setAutoRotate] = useState(true);
    const [pulseEffect, setPulseEffect] = useState({});
    const [centerOffset, setCenterOffset] = useState({ x: 0, y: 0 });
    const [activeNodeId, setActiveNodeId] = useState(null);
    const containerRef = useRef(null);
    const orbitRef = useRef(null);
    const nodeRefs = useRef({});

    const handleContainerClick = (e) => {
        if (e.target === containerRef.current || e.target === orbitRef.current) {
            setExpandedItems({});
            setActiveNodeId(null);
            setPulseEffect({});
            setAutoRotate(true);
        }
    };

    const toggleItem = (id) => {
        setExpandedItems((prev) => {
            const newState = { ...prev };
            Object.keys(newState).forEach((key) => {
                if (parseInt(key) !== id) {
                    newState[parseInt(key)] = false;
                }
            });

            newState[id] = !prev[id];

            if (!prev[id]) {
                setActiveNodeId(id);
                setAutoRotate(false);

                const relatedItems = getRelatedItems(id);
                const newPulseEffect = {};
                relatedItems.forEach((relId) => {
                    newPulseEffect[relId] = true;
                });
                setPulseEffect(newPulseEffect);

                centerViewOnNode(id);
            } else {
                setActiveNodeId(null);
                setAutoRotate(true);
                setPulseEffect({});
            }

            return newState;
        });
    };

    useEffect(() => {
        let rotationTimer;

        if (autoRotate && viewMode === "orbital") {
            rotationTimer = setInterval(() => {
                setRotationAngle((prev) => {
                    const newAngle = (prev + 0.3) % 360;
                    return Number(newAngle.toFixed(3));
                });
            }, 50);
        }

        return () => {
            if (rotationTimer) {
                clearInterval(rotationTimer);
            }
        };
    }, [autoRotate, viewMode]);

    const centerViewOnNode = (nodeId) => {
        if (viewMode !== "orbital" || !nodeRefs.current[nodeId]) return;

        const nodeIndex = timelineData.findIndex((item) => item.id === nodeId);
        const totalNodes = timelineData.length;
        const targetAngle = (nodeIndex / totalNodes) * 360;

        setRotationAngle(270 - targetAngle);
    };

    const calculateNodePosition = (index, total) => {
        const angle = ((index / total) * 360 + rotationAngle) % 360;
        const radius = 200;
        const radian = (angle * Math.PI) / 180;

        const x = radius * Math.cos(radian) + centerOffset.x;
        const y = radius * Math.sin(radian) + centerOffset.y;

        const zIndex = Math.round(100 + 50 * Math.cos(radian));
        const opacity = Math.max(
            0.4,
            Math.min(1, 0.4 + 0.6 * ((1 + Math.sin(radian)) / 2))
        );

        return { x, y, angle, zIndex, opacity };
    };

    const getRelatedItems = (itemId) => {
        const currentItem = timelineData.find((item) => item.id === itemId);
        return currentItem ? currentItem.relatedIds || [] : [];
    };

    const isRelatedToActive = (itemId) => {
        if (!activeNodeId) return false;
        const relatedItems = getRelatedItems(activeNodeId);
        return relatedItems.includes(itemId);
    };

    const isDark = backgroundColor === "bg-black" || backgroundColor.includes("black");
    const isOrange = backgroundColor.includes("orange");

    const getStatusStyles = (status) => {
        if (isOrange) {
            switch (status) {
                case "completed":
                    return "text-white bg-orange-700 border-orange-600";
                case "in-progress":
                    return "text-orange-700 bg-white border-orange-600";
                case "pending":
                    return "text-white bg-orange-400/50 border-orange-300/50";
                default:
                    return "text-white bg-orange-400/50 border-orange-300/50";
            }
        }
        switch (status) {
            case "completed":
                return "text-white bg-black border-white";
            case "in-progress":
                return "text-black bg-white border-black";
            case "pending":
                return "text-white bg-black/40 border-white/50";
            default:
                return "text-white bg-black/40 border-white/50";
        }
    };

    const centerGradient = isDark
        ? "from-purple-500 via-blue-500 to-teal-500"
        : isOrange
            ? "from-orange-600 via-orange-700 to-red-600"
            : "from-orange-500 via-orange-600 to-orange-700";
    const centerBg = isDark ? "bg-white/80" : isOrange ? "bg-white/90" : "bg-transparent";
    const borderColor = isDark ? "border-white" : isOrange ? "border-white" : "border-gray-900";

    return (
        <div
            className={`w-full h-full flex flex-col items-center justify-center ${backgroundColor} overflow-visible relative`}
            ref={containerRef}
            onClick={handleContainerClick}
        >
            {/* Header Section - Positioned at top */}
            {showHeader && (
                <div className="absolute top-16 left-0 right-0 z-30 px-8 md:px-12 lg:px-16">
                    <div className="max-w-2xl mx-auto text-center">
                        <h2 className={`text-3xl md:text-4xl lg:text-5xl font-bold leading-tight ${textColor} mb-2`}>
                            {headerTitle.includes('Kinesis') ? (
                                <>
                                    {headerTitle.split('Kinesis')[0]}
                                    <Highlighter action="underline" color="#f97316" strokeWidth={2.5} padding={2} multiline={false} isView={true}>
                                        Kinesis
                                    </Highlighter>
                                    {headerTitle.split('Kinesis')[1]}
                                </>
                            ) : (
                                headerTitle
                            )}
                        </h2>
                        <p className={`text-base md:text-lg leading-relaxed ${textColor}/80 mt-1 mb-4`}>
                            {headerDescription}
                        </p>
                    </div>
                </div>
            )}

            <div className="relative w-full h-full flex items-center justify-center overflow-visible" style={{ transform: showHeader ? 'translateY(10%)' : 'translateY(-8%)' }}>
                <div
                    className="absolute w-full h-full flex items-center justify-center overflow-visible"
                    ref={orbitRef}
                    style={{
                        perspective: "1000px",
                        transform: `translate(${centerOffset.x}px, ${centerOffset.y}px)`,
                    }}
                >
                    <div className={`absolute w-20 h-20 rounded-full ${isDark ? "bg-gradient-to-br from-purple-500 via-blue-500 to-teal-500" : isOrange ? "bg-gradient-to-br from-orange-600 via-orange-700 to-red-600" : "bg-white"} animate-pulse flex items-center justify-center z-10`}>
                        <div className={`absolute w-24 h-24 rounded-full border ${isDark ? "border-white/20" : isOrange ? "border-white/40" : "border-orange-500/30"} animate-ping ${isOrange ? "opacity-80" : "opacity-70"}`}></div>
                        <div
                            className={`absolute w-28 h-28 rounded-full border ${isDark ? "border-white/10" : isOrange ? "border-white/30" : "border-orange-500/20"} animate-ping ${isOrange ? "opacity-60" : "opacity-50"}`}
                            style={{ animationDelay: "0.5s" }}
                        ></div>
                        <div className={`w-36 h-36 rounded-full ${centerBg} backdrop-blur-md flex items-center justify-center p-1.5`}>
                            <img
                                src={kinesisLogo}
                                alt="Kinesis Logo"
                                className="w-full h-full object-contain"
                            />
                        </div>
                    </div>

                    <div className={`absolute w-96 h-96 rounded-full border ${isDark ? "border-white/10" : isOrange ? "border-white/30" : "border-orange-500/20"}`}></div>

                    {timelineData.map((item, index) => {
                        const position = calculateNodePosition(index, timelineData.length);
                        const isExpanded = expandedItems[item.id];
                        const isRelated = isRelatedToActive(item.id);
                        const isPulsing = pulseEffect[item.id];
                        const Icon = item.icon;

                        const nodeStyle = {
                            transform: `translate(${position.x}px, ${position.y}px)`,
                            zIndex: isExpanded ? 200 : position.zIndex,
                            opacity: isExpanded ? 1 : position.opacity,
                        };

                        return (
                            <div
                                key={item.id}
                                ref={(el) => (nodeRefs.current[item.id] = el)}
                                className="absolute transition-all duration-700 cursor-pointer overflow-visible"
                                style={nodeStyle}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleItem(item.id);
                                }}
                            >
                                <div
                                    className={`absolute rounded-full -inset-1 ${isPulsing ? "animate-pulse duration-1000" : ""
                                        }`}
                                    style={{
                                        background: isOrange
                                            ? `radial-gradient(circle, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 70%)`
                                            : isDark
                                                ? `radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)`
                                                : `transparent`,
                                        width: `${item.energy * 0.5 + 40}px`,
                                        height: `${item.energy * 0.5 + 40}px`,
                                        left: `-${(item.energy * 0.5 + 40 - 40) / 2}px`,
                                        top: `-${(item.energy * 0.5 + 40 - 40) / 2}px`,
                                    }}
                                ></div>

                                <div
                                    className={`
                                        w-10 h-10 rounded-full flex items-center justify-center
                                        ${isExpanded
                                            ? isDark ? "bg-white text-black" : isOrange ? "bg-white text-orange-600" : "bg-orange-50 text-orange-700"
                                            : isRelated
                                                ? isDark ? "bg-white/50 text-black" : isOrange ? "bg-white/70 text-orange-700" : "bg-gray-900/50 text-orange-700"
                                                : isDark ? " text-white" : isOrange ? "bg-white/90 text-orange-700" : "bg-orange-500/10 text-orange-800 border-orange-500/30"
                                        }
                                        border-2 
                                        ${isExpanded
                                            ? isDark ? "border-white shadow-lg shadow-white/30" : isOrange ? "border-white shadow-lg shadow-white/50" : "border-orange-400 shadow-lg shadow-gray-900/30"
                                            : isRelated
                                                ? isDark ? "border-white animate-pulse" : isOrange ? "border-white animate-pulse" : "border-gray-900 animate-pulse"
                                                : isDark ? "border-white/40" : isOrange ? "border-white/60" : "border-orange-500/50"
                                        }
                                        transition-all duration-300 transform
                                        ${isExpanded ? "scale-150" : ""}
                                    `}
                                >
                                    <Icon size={16} className={isDark ? "" : isOrange ? "" : "text-orange-800"} />
                                </div>

                                <div
                                    className={`
                                        absolute top-12 whitespace-nowrap
                                        text-xs font-semibold tracking-wider
                                        transition-all duration-300
                                        ${isExpanded
                                            ? (isDark ? "text-white" : isOrange ? "text-orange-700" : "text-orange-800") + " scale-125"
                                            : (isDark ? "text-white/70" : isOrange ? "text-orange-600/80" : "text-orange-800/90")}
                                    `}
                                    style={{
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                    }}
                                >
                                    {item.title}
                                </div>

                                {isExpanded && (
                                    <Card 
                                        className={`
                                            absolute top-20 left-1/2 -translate-x-1/2 w-64 
                                            ${isDark ? "bg-black/90" : isOrange ? "bg-white" : "bg-white/90"} 
                                            backdrop-blur-lg 
                                            ${isDark ? "border-white/30" : isOrange ? "border-orange-300/50" : "border-orange-500/30"} 
                                            shadow-xl 
                                            ${isDark ? "shadow-white/10" : isOrange ? "shadow-orange-900/30" : "shadow-orange-900/10"} overflow-visible
                                        `}
                                    >
                                        
                                        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 w-px h-3 ${isDark ? "bg-white/50" : isOrange ? "bg-orange-400/70" : "bg-orange-500/50"}`}></div>
                                        
                                        <CardHeader 
                                            className={`pb-2 ${isOrange ? "bg-orange-50/50" : isDark ? "" : "bg-orange-50/30"}`}
                                        >

                                            {/* <div className="flex justify-between items-center">
                                                <Badge
                                                className={`px-2 text-xs ${getStatusStyles(
                                                    item.status
                                                )}`}
                                                >
                                                {item.status === "completed"
                                                    ? "COMPLETE"
                                                    : item.status === "in-progress"
                                                    ? "IN PROGRESS"
                                                    : "PENDING"}
                                                </Badge>
                                                <span className={`text-xs font-mono ${isOrange ? "text-orange-700/70" : isDark ? textColor + "/50" : "text-orange-700/70"}`}>
                                                {item.date}
                                                </span>
                                            </div> */}

                                            <CardTitle className={`text-sm ${isOrange ? "text-orange-900" : isDark ? textColor : "text-orange-900"}`}>
                                                {item.title}
                                            </CardTitle>

                                        </CardHeader>
    
                                        <CardContent className={`text-xs ${isOrange ? "text-orange-800/90" : isDark ? textColor + "/80" : "text-orange-800/90"}`}>
                                            <p>{item.content}</p>

                                            {/* 
                                            {item.relatedIds.length > 0 && (
                                                <div className={`mt-4 pt-3 border-t ${isDark ? "border-white/10" : isOrange ? "border-orange-200/30" : "border-orange-200/30"}`}>
                                                    <div className={`flex items-center mb-2 ${isDark ? textColor : isOrange ? "text-orange-900" : "text-orange-900"}`}>
                                                        <Link size={10} className={`${isDark ? textColor + "/70" : isOrange ? "text-orange-700/70" : "text-orange-700/70"} mr-1`} />
                                                        <h4 className={`text-xs uppercase tracking-wider font-medium ${isDark ? textColor + "/70" : isOrange ? "text-orange-700/70" : "text-orange-700/70"}`}>
                                                            Next
                                                        </h4>
                                                    </div>
                                                    <div className="flex flex-wrap gap-1">
                                                        {item.relatedIds.map((relatedId) => {
                                                            const relatedItem = timelineData.find(
                                                                (i) => i.id === relatedId
                                                            );
                                                            return (
                                                                <Button
                                                                    key={relatedId}
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className={`flex items-center h-6 px-2 py-0 text-xs rounded-none ${isDark ? "border-white/20 bg-transparent hover:bg-white/10 text-white/80 hover:text-white" : isOrange ? "border-orange-300/40 bg-transparent hover:bg-orange-200/20 text-orange-800/80 hover:text-orange-900" : "border-orange-500/30 bg-transparent hover:bg-orange-100/50 text-orange-700/80 hover:text-orange-900"} transition-all`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleItem(relatedId);
                                                                    }}
                                                                >
                                                                    {relatedItem?.title}
                                                                    <ArrowRight
                                                                        size={8}
                                                                        className={`ml-1 ${isDark ? textColor + "/60" : isOrange ? "text-orange-600/60" : "text-orange-600/60"}`}
                                                                    />
                                                                </Button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )} 
                                            */}
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

